import React from 'react';
import { format, parseISO } from 'date-fns';
import {
  formatJobTime,
  formatDuration,
  groupJobsByTeam,
  calculateRouteStats,
  calculateShiftTimes,
  jobStatuses,
  serviceTypes
} from '../utils/jobData';
import './DispatchPrintView.css';

function DispatchPrintView({ jobs, selectedDate, timeOffRequests = [], getEmployeeById }) {
  const dayJobs = (jobs || []).filter(j => j.date === selectedDate);
  const assigned = dayJobs.filter(j => j.team && j.team.length > 0);
  const unassigned = dayJobs.filter(j => !j.team || j.team.length === 0);

  const teams = Object.values(groupJobsByTeam(assigned)).sort((a, b) => {
    const aFirst = a.jobs[0]?.scheduledTime || '';
    const bFirst = b.jobs[0]?.scheduledTime || '';
    return aFirst.localeCompare(bFirst);
  });

  const totalRevenue = dayJobs.reduce((sum, j) => sum + (j.price || 0), 0);
  const totalDuration = dayJobs.reduce((sum, j) => sum + (j.duration || 0), 0);

  const dateObj = selectedDate ? parseISO(selectedDate) : new Date();
  const dayHeading = format(dateObj, 'EEEE, MMMM d, yyyy');

  const timeOffToday = (timeOffRequests || []).filter(t =>
    (t.status === 'approved' || t.status === 'pending') &&
    t.startDate <= selectedDate &&
    t.endDate >= selectedDate
  );

  const renderJobRow = (job, idx) => {
    const status = jobStatuses[job.status]?.label || 'Scheduled';
    const service = serviceTypes[job.serviceType]?.label || job.serviceType || '';
    const cityState = [job.city, job.state].filter(Boolean).join(', ');
    return (
      <tr key={job.id} className={`print-job-row status-${job.status || 'scheduled'}`}>
        <td className="col-stop">{idx + 1}</td>
        <td className="col-time">
          <div className="time-main">{formatJobTime(job.scheduledTime)}</div>
          {job.scheduledEndTime && (
            <div className="time-end">→ {formatJobTime(job.scheduledEndTime)}</div>
          )}
          <div className="time-duration">{formatDuration(job.duration || 0)}</div>
        </td>
        <td className="col-client">
          <div className="client-name">
            {job.clientName}
            {job.isFirstClean && <span className="badge-first">FIRST CLEAN</span>}
          </div>
          {job.clientPhone && <div className="client-phone">{job.clientPhone}</div>}
        </td>
        <td className="col-address">
          <div>{job.address}</div>
          {cityState && <div className="address-city">{cityState} {job.zip || ''}</div>}
        </td>
        <td className="col-service">
          <div>{service}</div>
          <div className="job-status">{status}</div>
        </td>
        <td className="col-price">${(job.price || 0).toLocaleString()}</td>
        <td className="col-notes">
          {job.notes ? <div className="notes-text">{job.notes}</div> : <span className="notes-empty">—</span>}
        </td>
      </tr>
    );
  };

  return (
    <div className="dispatch-print-view" aria-hidden="true">
      <div className="print-page-header">
        <div>
          <h1>Daily Dispatch</h1>
          <h2>{dayHeading}</h2>
        </div>
        <div className="print-summary">
          <div><strong>{dayJobs.length}</strong> jobs</div>
          <div><strong>{teams.length}</strong> team{teams.length !== 1 ? 's' : ''}</div>
          <div><strong>{totalDuration}</strong> hrs</div>
          <div><strong>${totalRevenue.toLocaleString()}</strong></div>
        </div>
      </div>

      {timeOffToday.length > 0 && (
        <div className="print-time-off">
          <strong>Time Off:</strong>{' '}
          {timeOffToday.map((req, i) => {
            const emp = getEmployeeById ? getEmployeeById(req.employeeId) : null;
            const name = emp ? `${emp.firstName} ${emp.lastName?.[0] || ''}.` : 'Unknown';
            const typeLabels = { pto: 'PTO', sick: 'Sick', personal: 'Personal', unpaid: 'Unpaid' };
            const tag = typeLabels[req.type] || req.type;
            return (
              <span key={req.id} className="time-off-pill">
                {name} ({tag}{req.status === 'pending' ? ', pending' : ''}){i < timeOffToday.length - 1 ? ',' : ''}
              </span>
            );
          })}
        </div>
      )}

      {teams.length === 0 && unassigned.length === 0 && (
        <div className="print-empty">No jobs scheduled for this day.</div>
      )}

      {teams.map((team, teamIndex) => {
        const stats = calculateRouteStats(team.jobs);
        const shift = calculateShiftTimes(team.jobs);
        return (
          <section key={teamIndex} className="print-team">
            <header className="print-team-header">
              <div className="team-title">
                <h3>Team: {team.teamKey}</h3>
                <span className="team-job-count">{team.jobs.length} stop{team.jobs.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="team-stats">
                {shift && (
                  <span className="team-shift">
                    Shift: <strong>{shift.shiftStart}</strong> – <strong>{shift.shiftEnd}</strong>
                    {' '}({shift.totalShiftHours}h)
                  </span>
                )}
                <span>Drive: <strong>{stats.totalDriveTime}</strong> min · {stats.totalMiles} mi</span>
                <span>Revenue: <strong>${stats.revenue.toLocaleString()}</strong></span>
              </div>
            </header>

            <table className="print-job-table">
              <thead>
                <tr>
                  <th className="col-stop">#</th>
                  <th className="col-time">Time</th>
                  <th className="col-client">Client</th>
                  <th className="col-address">Address</th>
                  <th className="col-service">Service</th>
                  <th className="col-price">Price</th>
                  <th className="col-notes">Notes</th>
                </tr>
              </thead>
              <tbody>
                {team.jobs.map((job, idx) => renderJobRow(job, idx))}
              </tbody>
            </table>
          </section>
        );
      })}

      {unassigned.length > 0 && (
        <section className="print-team print-unassigned">
          <header className="print-team-header">
            <div className="team-title">
              <h3>Unassigned</h3>
              <span className="team-job-count">{unassigned.length} stop{unassigned.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="team-stats unassigned-warning">Needs team assignment</div>
          </header>
          <table className="print-job-table">
            <thead>
              <tr>
                <th className="col-stop">#</th>
                <th className="col-time">Time</th>
                <th className="col-client">Client</th>
                <th className="col-address">Address</th>
                <th className="col-service">Service</th>
                <th className="col-price">Price</th>
                <th className="col-notes">Notes</th>
              </tr>
            </thead>
            <tbody>
              {unassigned.map((job, idx) => renderJobRow(job, idx))}
            </tbody>
          </table>
        </section>
      )}

      <div className="print-footer-notes">
        <h4>Manager Notes</h4>
        <div className="note-lines">
          <div className="note-line" />
          <div className="note-line" />
          <div className="note-line" />
          <div className="note-line" />
        </div>
        <div className="print-footer-meta">
          Two Sisters Cleaning &amp; Organizing · Printed {format(new Date(), 'M/d/yyyy h:mm a')}
        </div>
      </div>
    </div>
  );
}

export default DispatchPrintView;
